import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Printer,
  Bluetooth,
  Usb,
  WifiOff,
  Loader2,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';
import { PRINTER_CONFIG } from './posUtils';

const PrinterSetupDialog = ({
  open,
  onOpenChange,
  printerSettings,
  setPrinterSettings,
  savePrinterSettings,
  isBluetoothConnected,
  isBluetoothSupported,
  isConnecting,
  isPrinting,
  connectBluetoothPrinter,
  disconnectBluetoothPrinter,
  printViaBluetooth,
  lastReceipt,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer size={20} />
            Printer Setup - {PRINTER_CONFIG.brand} {PRINTER_CONFIG.model}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Bluetooth Connection Status */}
          <div className={`p-4 rounded-lg border-2 ${
            isBluetoothConnected 
              ? 'bg-blue-50 border-blue-300' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {isBluetoothConnected ? (
                  <>
                    <Bluetooth className="text-blue-600" size={24} />
                    <div>
                      <span className="font-semibold text-blue-700">Bluetooth Connected</span>
                      <p className="text-sm text-blue-600">{printerSettings.printerName}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <WifiOff className="text-slate-400" size={24} />
                    <div>
                      <span className="font-medium text-slate-700">Bluetooth Not Connected</span>
                      <p className="text-sm text-slate-500">Connect for direct tablet printing</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {isBluetoothSupported ? (
              <div className="flex gap-2 mt-3">
                {isBluetoothConnected ? (
                  <>
                    <Button onClick={disconnectBluetoothPrinter} variant="outline" size="sm" className="flex-1">
                      Disconnect
                    </Button>
                    <Button 
                      onClick={() => lastReceipt && printViaBluetooth(lastReceipt)}
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={isPrinting || !lastReceipt}
                    >
                      {isPrinting ? 'Printing...' : 'Test Print'}
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={connectBluetoothPrinter}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={isConnecting}
                  >
                    {isConnecting ? (
                      <><Loader2 size={18} className="mr-2 animate-spin" /> Connecting...</>
                    ) : (
                      <><Bluetooth size={18} className="mr-2" /> Connect Bluetooth Printer</>
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-amber-600 mt-2">
                Bluetooth not supported in this browser. Use Chrome on Android for Bluetooth printing.
              </p>
            )}
          </div>

          {/* Printer Status */}
          <div className={`p-3 rounded-lg ${printerSettings.isConfigured ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2">
              {printerSettings.isConfigured ? (
                <>
                  <CheckCircle2 className="text-emerald-600" size={20} />
                  <span className="font-medium text-emerald-700">Printer Configured</span>
                </>
              ) : (
                <>
                  <HelpCircle className="text-amber-600" size={20} />
                  <span className="font-medium text-amber-700">Printer Setup Required</span>
                </>
              )}
            </div>
            <p className="text-sm mt-1 text-slate-600">
              Current printer: <strong>{printerSettings.printerName || PRINTER_CONFIG.model}</strong>
            </p>
          </div>

          {/* Printer Specs */}
          <div className="bg-slate-50 p-3 rounded-lg">
            <h4 className="font-medium text-slate-700 mb-2">Printer Specifications</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Brand:</span> {PRINTER_CONFIG.brand}</div>
              <div><span className="text-slate-500">Model:</span> {PRINTER_CONFIG.model}</div>
              <div><span className="text-slate-500">Paper Width:</span> {PRINTER_CONFIG.paperWidth}</div>
              <div><span className="text-slate-500">Connection:</span> {PRINTER_CONFIG.interface}</div>
            </div>
          </div>

          {/* Setup Instructions */}
          <div className="space-y-3">
            <h4 className="font-medium text-slate-700">Setup Instructions</h4>
            <div className="space-y-2">
              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium text-blue-800">Connect the Printer</p>
                  <p className="text-sm text-blue-700">Connect via USB cable or pair via Bluetooth</p>
                  <div className="flex gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-100 px-2 py-0.5 rounded">
                      <Usb size={12} /> USB
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-100 px-2 py-0.5 rounded">
                      <Bluetooth size={12} /> Bluetooth
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium text-purple-800">Install Printer (Android)</p>
                  <p className="text-sm text-purple-700">
                    Go to <strong>Settings - Connected devices - Connection preferences - Printing</strong>
                  </p>
                  <p className="text-sm text-purple-700 mt-1">Enable the printing service and add your printer</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-emerald-50 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium text-emerald-800">Set as Default Printer</p>
                  <p className="text-sm text-emerald-700">
                    In printing settings, select <strong>{PRINTER_CONFIG.brand} {PRINTER_CONFIG.model}</strong> as your default printer
                  </p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-amber-50 rounded-lg">
                <div className="flex-shrink-0 w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <p className="font-medium text-amber-800">Test Print</p>
                  <p className="text-sm text-amber-700">
                    Click "Print Receipt" on any sale. In the print dialog, select your printer and tap Print.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Remember Printer Setting */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium">Your Printer Name</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={printerSettings.printerName || ''}
                onChange={(e) => setPrinterSettings({...printerSettings, printerName: e.target.value})}
                placeholder="e.g., Kprinter JK-5802H"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Enter the name as it appears in your device's printer list</p>
          </div>

          {/* Mark as Configured */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-700">Mark printer as configured</p>
              <p className="text-xs text-slate-500">Check this once you've completed the setup steps above</p>
            </div>
            <Button
              variant={printerSettings.isConfigured ? "default" : "outline"}
              size="sm"
              onClick={() => savePrinterSettings({ isConfigured: !printerSettings.isConfigured })}
              className={printerSettings.isConfigured ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            >
              {printerSettings.isConfigured ? (
                <><CheckCircle2 size={16} className="mr-1" /> Configured</>
              ) : (
                "Mark as Done"
              )}
            </Button>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button 
            onClick={() => {
              savePrinterSettings({ printerName: printerSettings.printerName });
              onOpenChange(false);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrinterSetupDialog;
